import Image from 'next/image'
import {
  Body,
  Eyebrow,
  H3,
  Stack,
} from '@/components/site/primitives'
import { ActionSwapText } from '@/components/motion/action-swap'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { TrackedAnchor } from '@/components/analytics/TrackedAnchor.client'
import { valuationHref } from '@/lib/site/valuation-href'
import type { Broker } from '@/lib/data/types/broker'
import type { ReviewsSummary } from '@/lib/data/reviews/getReviews'

/**
 * Listing-detail broker contact card — the ONE sticky sidebar CTA. Shows the
 * contact broker's FULL contact info (click-to-call phone + email, name, title,
 * license, headshot), the primary "Schedule a tour" + Call/Text actions, and —
 * on large screens only — a short verified-Google-review social-proof block.
 *
 * The phone is the broker's Twilio business line (broker.phoneDirect): inbound is
 * recorded, logged to the CRM timeline, and forwarded to their cell.
 *
 * Reviews are brokerage-level (verified Google Business Profile) and exempt from
 * the brand-voice gate (real customer words, not our copy). Renders the rating +
 * count badge and one recent quote; the block is hidden entirely when there are
 * no reviews or none with text.
 *
 * Per CLAUDE.md brand voice: direct + specific, no marketing slop, no banned
 * tokens, no em-dash.
 */

type Props = {
  broker: Broker
  listingKey: string
  /** Brokerage Google reviews for the lg-only social-proof block. */
  reviews?: ReviewsSummary | null
  headline?: string
  body?: string
  primaryCta?: { href: string; label: string }
  /**
   * SITE-21. Null on market; 'sold' for Closed; 'unsold' for Expired, Canceled
   * and Withdrawn.
   *
   * The card's whole ask is a showing: "Tour" over "Tour requests usually get a
   * same-day reply", with Call and Text under it. On a home that is not for
   * sale that is three unfulfillable requests, and the call it starts ends with
   * "that one sold." Off market the card asks the two questions a broker CAN
   * answer — what happened with this listing, and what it means for the
   * reader's own house — and it builds no `tel:` or `sms:` URI, so the page's
   * only click-to-call is the brokerage line in the site footer.
   *
   * SOLD AND UNSOLD ARE NOT THE SAME QUESTION, and the first render proved it:
   * an EXPIRED listing carried "Questions about this sale? · Ask what this one
   * closed at and why" over a home that never sold (§0.5, looked at 2026-09-09).
   */
  offMarket?: 'sold' | 'unsold' | null
  className?: string
}

function digits(phone: string): string {
  return phone.replace(/[^\d]/g, '')
}

export function TextMattCTA({
  broker,
  listingKey,
  reviews,
  headline,
  body,
  primaryCta,
  offMarket = null,
  className,
}: Props) {
  const askHref = `/contact?listingKey=${encodeURIComponent(listingKey)}&intent=question`
  const tourHref =
    primaryCta?.href ??
    (offMarket ? askHref : `/contact?listingKey=${encodeURIComponent(listingKey)}&intent=tour`)
  const tourLabel =
    primaryCta?.label ??
    (offMarket === 'sold' ? 'Ask about this sale' : offMarket ? 'Ask about this home' : 'Tour')
  const phone = broker.phoneDirect ?? broker.phoneFub ?? null

  const quote = reviews?.reviews?.[0] ?? null
  const showProof = !!reviews && reviews.count > 0 && reviews.averageRating > 0

  return (
    <div
      className={className ?? undefined}
      style={{
        border: '1px solid var(--v3-navy)',
        background: 'var(--v3-cream)',
        padding: 'var(--v3-space-md)',
      }}
    >
      <Stack gap="default">
        <Eyebrow>Talk to a broker</Eyebrow>
        <H3>{headline ?? (offMarket === 'sold' ? 'Questions about this sale?' : 'Questions about this home?')}</H3>
        <Body size="small" tone="muted" className="leading-relaxed">
          {body ??
            (offMarket === 'sold'
              ? 'Ask what this one closed at and why, or what it says about the house you own.'
              : offMarket
                ? 'It came off the market without selling. Ask what happened, or what it means for the house you own.'
                : 'Tour requests usually get a same-day reply.')}
        </Body>

        {/* Broker — large photo on the left, all contact on the right */}
        <div className="flex gap-4 pt-1">
          <Image
            src={broker.headshotPng}
            alt={broker.fullName}
            width={96}
            height={120}
            className="shrink-0 self-start"
            style={{ width: 96, height: 120, objectFit: 'contain', objectPosition: 'top' }}
          />
          <div className="min-w-0 flex flex-col gap-1.5">
            <div>
              <div className="text-base font-semibold leading-tight" style={{ color: 'var(--navy)' }}>
                {broker.fullName}
              </div>
              <div className="text-xs leading-snug" style={{ color: 'var(--navy-70)' }}>
                {broker.title}
                {broker.licenseNumber ? (
                  <>
                    {' '}
                    <span className="tabular-nums">#{broker.licenseNumber}</span>
                  </>
                ) : null}
              </div>
            </div>
            {phone && !offMarket ? (
              <a
                href={`tel:${digits(phone)}`}
                className="flex items-center gap-2 text-sm font-semibold tabular-nums"
                style={{ color: 'var(--navy)' }}
              >
                <PhoneIcon />
                {phone}
              </a>
            ) : null}
            {broker.email ? (
              <a
                href={`mailto:${broker.email}`}
                className="flex items-center gap-2 text-xs break-all leading-snug"
                style={{ color: 'var(--navy-70)' }}
              >
                <MailIcon />
                {broker.email}
              </a>
            ) : null}
          </div>
        </div>

        <ButtonGroup
          aria-label={offMarket ? 'Ask about this listing' : 'Contact about this listing'}
          className="w-full gap-0 pt-1"
        >
          <Button variant="outline" size="lg" className="rounded-none first:rounded-l-lg last:rounded-r-lg" asChild>
            <TrackedAnchor
              href={tourHref}
              event={primaryCta ? 'click_cta' : offMarket ? 'ask_question_click' : 'schedule_tour_click'}
              eventParams={{
                listing_key: listingKey,
                surface: 'listing_broker_card',
                ...(primaryCta ? { cta: primaryCta.label } : {}),
              }}
            >
              <ActionSwapText value="tour">{tourLabel}</ActionSwapText>
            </TrackedAnchor>
          </Button>
          {offMarket ? (
            <Button variant="outline" size="lg" className="rounded-none first:rounded-l-lg last:rounded-r-lg" asChild>
              <a href={valuationHref('/listing')}>What is my home worth?</a>
            </Button>
          ) : (
            <>
              {phone ? (
                <Button variant="outline" size="lg" className="rounded-none first:rounded-l-lg last:rounded-r-lg" asChild>
                  <a href={`tel:${digits(phone)}`}>Call</a>
                </Button>
              ) : null}
              {phone ? (
                <Button variant="outline" size="lg" className="rounded-none first:rounded-l-lg last:rounded-r-lg" asChild>
                  <a href={`sms:${digits(phone)}`}>Text</a>
                </Button>
              ) : null}
            </>
          )}
        </ButtonGroup>

        {/* Social proof — large screens only (verified Google reviews) */}
        {showProof ? (
          <div
            className="hidden lg:block"
            style={{ borderTop: '1px solid color-mix(in srgb, var(--v3-navy) 14%, transparent)', paddingTop: 14, marginTop: 2 }}
          >
            <div className="flex items-center gap-2">
              <span aria-hidden style={{ color: 'var(--navy)', letterSpacing: '0.04em', fontSize: '0.95rem' }}>
                {'★'.repeat(Math.round(reviews!.averageRating))}
              </span>
              <span className="text-xs font-semibold tabular-nums" style={{ color: 'var(--navy)' }}>
                {reviews!.averageRating.toFixed(1)}
              </span>
              {/* Flex gap spaces these on screen; textContent (crawlers, copy)
                  read "5.025 Google reviews" without a real space (W8). */}{' '}
              <span className="text-xs" style={{ color: 'var(--navy-70)' }}>
                {reviews!.count} Google reviews
              </span>
            </div>
            {quote ? (
              <blockquote
                className="mt-2 text-xs leading-normal"
                style={{ color: 'color-mix(in srgb, var(--v3-navy) 78%, transparent)' }}
              >
                {truncate(quote.text, 150)}
                {quote.reviewerName ? (
                  <cite className="mt-1 block not-italic text-xs" style={{ color: 'var(--navy-70)' }}>
                    {quote.reviewerName}
                  </cite>
                ) : null}
              </blockquote>
            ) : null}
          </div>
        ) : null}
      </Stack>
    </div>
  )
}

/** Trim to the last word boundary under `max` and add an ellipsis. */
function truncate(s: string, max: number): string {
  const t = s.trim()
  if (t.length <= max) return `“${t}”`
  const cut = t.slice(0, max)
  const lastSpace = cut.lastIndexOf(' ')
  return `“${(lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trim()}…”`
}

function PhoneIcon() {
  return (
    <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="shrink-0">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  )
}

function MailIcon() {
  return (
    <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="shrink-0">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 6L2 7" />
    </svg>
  )
}
